import HeroCircuit from "@/components/HeroCircuit";
import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Check,
  ArrowRight,
  ExternalLink,
  MessageCircle,
  Stethoscope,
  Shield,
  Phone,
  Calendar,
  FileText,
  Search,
  Globe,
} from "lucide-react";
import { projects } from "@/data/portfolio";
import { localizedPath, type LangCode } from "@/i18n/config";
import { SITES_PARA_MEDICOS_DICTS } from "@/i18n/pages/sites-para-medicos";

const planSlug = "site-para-medicos";

const BENEFIT_ICONS = [Shield, Phone, Calendar, Search, FileText, Globe];

const medicalProjects = projects.filter(
  (p) => p.categoria === "Saúde" || p.categoria === "Estética & Bem-estar",
);

function ProjectThumb({
  nome,
  imagem,
  loading = "lazy",
}: {
  nome: string;
  imagem: string;
  loading?: "lazy" | "eager";
}) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div className="flex aspect-[4/3] w-full items-center justify-center bg-primary p-4 text-center">
        <span className="text-xl font-bold text-primary-foreground font-display">{nome}</span>
      </div>
    );
  }

  return (
    <img
      src={imagem}
      alt={nome}
      width={800}
      height={600}
      loading={loading}
      decoding="async"
      onError={() => setFailed(true)}
      className="aspect-[4/3] w-full bg-muted object-cover"
    />
  );
}

export function SitesParaMedicosContent({ code }: { code: LangCode }) {
  const t = SITES_PARA_MEDICOS_DICTS[code] ?? SITES_PARA_MEDICOS_DICTS.en;
  const contato = localizedPath(code, "/contato");
  const portfolio = localizedPath(code, "/portfolio");
  const waHref = `https://wa.me/554591391239?text=${encodeURIComponent(t.whatsappMessage)}`;

  return (
    <main id="conteudo" className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden bg-ocean-900 text-hero-foreground">
        <div className="pointer-events-none absolute -top-24 left-1/4 h-96 w-96 rounded-full bg-hero-glow-cyan blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-24 right-1/4 h-96 w-96 rounded-full bg-hero-glow-blue blur-[120px]" />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.5) 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />
        <HeroCircuit />

        <div className="relative z-10 mx-auto flex max-w-6xl flex-col items-center px-4 py-24 text-center sm:px-6 sm:py-32 lg:px-8">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-hero-price-badge-border bg-hero-price-badge-bg px-4 py-1.5">
            <Stethoscope className="h-4 w-4 text-hero-accent" />
            <span className="text-sm font-semibold uppercase tracking-wide text-hero-accent">
              {t.badge}
            </span>
          </div>

          <h1 className="max-w-4xl text-4xl font-extrabold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl font-display">
            {t.h1}
          </h1>

          <p className="mt-6 max-w-2xl text-lg text-hero-foreground-muted">{t.heroDesc}</p>

          <div className="mt-10 flex flex-col items-center rounded-2xl border border-hero-glass-border bg-hero-glass-bg p-8 shadow-2xl backdrop-blur-xl sm:mt-12">
            <span className="mb-2 text-sm font-semibold uppercase tracking-widest text-hero-foreground-muted">
              {t.planBadge}
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-hero-foreground">R$</span>
              <span className="text-5xl font-extrabold tracking-tighter text-hero-foreground sm:text-6xl">
                300
              </span>
              <span className="text-xl font-medium text-hero-accent">/mês</span>
            </div>
            <p className="mt-2 text-sm text-hero-foreground-muted">{t.planCancel}</p>
          </div>

          <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:mt-12">
            <Button
              asChild
              size="lg"
              variant="inverse"
              className="shadow-xl shadow-hero-accent/20"
            >
              <a href={waHref} target="_blank" rel="noopener noreferrer" aria-label={t.ariaWhatsapp}>
                <MessageCircle className="mr-2 h-4 w-4" /> {t.ctaWhatsapp}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="inverseOutline"
            >
              <Link to={portfolio}>{t.ctaPortfolio}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Social proof stats */}
      <section className="border-y border-border bg-secondary">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-8 text-center sm:grid-cols-3">
            {t.stats.map((stat) => (
              <div key={stat.label}>
                <p className="text-4xl font-bold text-primary-strong font-display">{stat.value}</p>
                <p className="mt-2 text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl font-display">
            {t.benefitsHeading}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">{t.benefitsDesc}</p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {t.benefits.map((benefit, i) => {
            const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length]!;
            return (
              <Card key={benefit.title} className="border-border/60 bg-card transition-shadow hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary-strong">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-4 text-xl font-semibold text-card-foreground font-display">{benefit.title}</h3>
                  <p className="mt-2 text-muted-foreground">{benefit.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Portfolio proof */}
      <section className="border-t border-border bg-muted/30 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-sm font-semibold uppercase tracking-wider text-primary-strong">{t.portfolioLabel}</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl font-display">
              {t.portfolioHeading}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">{t.portfolioDesc}</p>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {medicalProjects.map((project) => (
              <Card
                key={project.slug}
                className="group flex flex-col overflow-hidden border-border/60 bg-card p-0 transition-[transform,box-shadow] hover:-translate-y-1 hover:shadow-xl"
              >
                <ProjectThumb nome={project.nome} imagem={project.imagem} loading="lazy" />
                <CardContent className="flex flex-1 flex-col p-6">
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary-strong">{project.categoria}</p>
                  <h3 className="mt-2 text-xl font-semibold text-card-foreground font-display">{project.nome}</h3>
                  <p className="mt-2 flex-1 text-muted-foreground">{project.descricao}</p>
                  <div className="mt-5 flex flex-wrap items-center gap-3">
                    <Button asChild size="sm" variant="outline">
                      <a href={project.url} target="_blank" rel="noopener noreferrer">
                        {t.viewSite} <ExternalLink className="ml-1 h-3 w-3" />
                      </a>
                    </Button>
                    <a
                      href={`https://wa.me/554591391239?text=${encodeURIComponent(
                        t.wantSimilarMessage.replace("{name}", project.nome).replace("{url}", project.url),
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-primary-strong hover:underline"
                    >
                      {t.wantSimilar}
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Button asChild size="lg" variant="outline">
              <Link to={portfolio}>
                {t.viewFullPortfolio} <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl font-display">
            {t.pricingHeading}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">{t.pricingDesc}</p>
        </div>

        <Card className="mt-8 overflow-hidden border-2 border-primary shadow-2xl">
          <div className="bg-primary p-8 text-center text-primary-foreground">
            <h3 className="text-3xl font-bold font-display">{t.planName}</h3>
            <div className="mt-4 flex items-baseline justify-center gap-2">
              <span className="text-5xl font-bold tracking-tight">R$ 300</span>
              <span className="text-lg text-primary-foreground/80">/mês</span>
            </div>
            <p className="mt-2 text-sm text-primary-foreground/80">{t.planCancel}</p>
          </div>
          <CardContent className="p-8">
            <ul className="grid gap-4 sm:grid-cols-2">
              {t.planFeatures.map((feature) => (
                <li key={feature} className="flex items-start gap-3">
                  <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary-strong">
                    <Check className="h-3 w-3" />
                  </div>
                  <span className="text-foreground">{feature}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 text-center">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to={contato} search={{ plano: planSlug }}>
                  {t.ctaPreview} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="text-center text-3xl font-bold tracking-tight text-foreground font-display">
          {t.faqHeading}
        </h2>
        <div className="mt-8 rounded-2xl border border-border bg-card p-2">
          <Accordion type="single" collapsible className="w-full">
            {t.faqs.map((faq, index) => (
              <AccordionItem key={faq.question} value={`item-${index}`} className="border-border/60 px-4">
                <AccordionTrigger className="text-left text-lg font-medium text-foreground hover:text-primary-strong">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-gradient-to-br from-cta-from to-cta-to py-16 text-hero-foreground">
        <HeroCircuit />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight font-display">{t.finalHeading}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-hero-foreground-muted">{t.finalDesc}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Button
              asChild
              size="lg"
              variant="inverse"
            >
              <a href={waHref} target="_blank" rel="noopener noreferrer" aria-label={t.ariaWhatsapp}>
                <MessageCircle className="mr-2 h-4 w-4" /> {t.finalWhatsapp}
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="inverseOutline"
            >
              <Link to={contato} search={{ plano: planSlug }}>
                {t.finalForm}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
